import { useEffect, useState } from 'react';
import storage, { adminTokenChangedEvent } from 'utils/storage';
import { useUser } from 'utils/auth';

/**
 * Admin impersonation hooks
 *
 * @see useAdminImpersonationActivation — URL token, PIN modal, public-route cleanup; mount in `AppRoutes` only.
 * @see useAdminImpersonationSession — this hook; banner state + exit action for layout components.
 */

export type UseAdminImpersonationSessionResult = {
  /** Whether an admin impersonation JWT is stored locally. */
  isAdminMode: boolean;
  /** Clears admin session storage and refetches the authenticated user. */
  handleStopAdminMode: () => void;
};

/**
 * Active admin impersonation session state for layout UI (e.g. AdminBar).
 *
 * Use this when you only need to show or dismiss the “Admin Mode aktiv” banner.
 * It syncs with `localStorage` via `adminTokenChangedEvent` and does **not**:
 * - watch URL search params (`adminToken`, `sessionExpired`)
 * - run public-route cleanup effects
 * - manage the PIN modal
 *
 * Activation flow lives in {@link useAdminImpersonationActivation}, which must stay a **single**
 * instance at app level to avoid duplicate side effects and React update loops.
 *
 * @example AdminBar.tsx
 * const { isAdminMode, handleStopAdminMode } = useAdminImpersonationSession();
 *
 * @example AppRoutes.tsx
 * const { pendingAdminToken, handlePinSuccess, handlePinClose } = useAdminImpersonationActivation();
 */
export const useAdminImpersonationSession = (): UseAdminImpersonationSessionResult => {
  const { refetch } = useUser();
  const [isAdminMode, setIsAdminMode] = useState(storage.getAdminToken() !== null);

  useEffect(() => {
    const syncAdminMode = (): void => {
      setIsAdminMode(storage.getAdminToken() !== null);
    };

    window.addEventListener(adminTokenChangedEvent, syncAdminMode);
    window.addEventListener('storage', syncAdminMode);

    return () => {
      window.removeEventListener(adminTokenChangedEvent, syncAdminMode);
      window.removeEventListener('storage', syncAdminMode);
    };
  }, []);

  const handleStopAdminMode = (): void => {
    storage.clearAdminSession();
    refetch().catch(() => undefined);
  };

  return {
    isAdminMode,
    handleStopAdminMode,
  };
};
